import { ChevronRight, Folder, FolderCheck, Loader2 } from 'lucide-react';
import { useFileExplorer } from '@speles7172/file-console';
import { EntityModal } from '../common/EntityModal';
import { fileTransport } from '../../api/files';

export interface PickedFolder {
  nodeId: string;
  /** The path from the picker's root, for showing what was chosen. */
  path: string;
}

export interface FolderPickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (folder: PickedFolder) => void;
  /** Where browsing starts, and the highest place that may be chosen. */
  rootNodeId: string;
  title?: string;
  /** The button's words — what choosing does. */
  confirmLabel?: string;
}

/**
 * Choose one folder of the file library: walk into folders, then choose the
 * one you are standing in — the root included.
 *
 * `FilePickerModal`'s sibling, for the same reason it is built on
 * `useFileExplorer` rather than `<FileExplorer>`: the browsing is the
 * package's (the tree, the permissions the server computed) and the pixels are
 * ours, because this opens inside a screen built here.
 */
export function FolderPickerModal({
  open,
  onClose,
  onPick,
  rootNodeId,
  title = 'Choose a folder',
  confirmLabel = 'Choose this folder',
}: FolderPickerModalProps) {
  const explorer = useFileExplorer({ transport: fileTransport, rootNodeId });
  const {
    nodeId,
    nodes,
    status,
    breadcrumbs,
    open: openNode,
    hasMoreNodes,
    loadMoreNodes,
  } = explorer;
  const path = breadcrumbs.map((crumb) => crumb.name).join(' / ');

  const choose = () => {
    onPick({ nodeId, path });
    onClose();
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      overlayClassName="z-[60]"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xs text-gray-500" data-testid="folder-picker-path">
            {path}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 sm:min-h-9 px-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={choose}
              disabled={status !== 'ready'}
              className="min-h-11 sm:min-h-9 flex items-center gap-1.5 px-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40"
            >
              <FolderCheck size={15} />
              {confirmLabel}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {breadcrumbs.length > 1 && (
          <nav
            aria-label="Folder path"
            className="flex flex-wrap items-center gap-1 text-xs text-gray-500"
          >
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.nodeId} className="flex items-center gap-1">
                {index > 0 && <ChevronRight size={12} />}
                <button
                  type="button"
                  onClick={() => openNode(crumb.nodeId)}
                  className="min-h-8 hover:text-primary-600"
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>
        )}

        {status === 'loading' && (
          <p className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        )}
        {status === 'error' && (
          <p role="alert" className="py-4 text-sm text-red-600">
            {explorer.error ?? 'The folders could not be loaded.'}
          </p>
        )}

        {status === 'ready' && (
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
            {nodes.length === 0 && (
              <p className="px-3 py-4 text-sm text-gray-500">
                No folders inside. Choose this one, or go back up.
              </p>
            )}
            {nodes.map((node) => (
              <button
                key={node.nodeId}
                type="button"
                onClick={() => openNode(node.nodeId)}
                className="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <Folder size={15} className="flex-shrink-0 text-primary-600" />
                <span className="flex-1 truncate">{node.name}</span>
                <ChevronRight size={14} className="text-gray-300" />
              </button>
            ))}
            {hasMoreNodes && (
              <button
                type="button"
                onClick={loadMoreNodes}
                className="min-h-11 w-full px-3 py-2 text-sm text-primary-600 hover:bg-gray-50"
              >
                Show more
              </button>
            )}
          </div>
        )}
      </div>
    </EntityModal>
  );
}
